import * as React from "react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useContext, useLayoutEffect } from "react";
import camelCase from "lodash/camelCase";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { styled, css } from "styled-components";
import { CELL_PADDING_HEADER } from "components/common/EntityDataTable/Constants";
import { DRAG_HANDLE_DEFAULT_TITLE } from "components/common/SortableList/DragHandle";
import DndStylesContext from "components/common/EntityDataTable/contexts/DndStylesContext";
import useHeaderSectionObserver from "components/common/EntityDataTable/hooks/useHeaderSectionObserver";
import HeaderActionsDropdown from "components/common/EntityDataTable/HeaderActionsDropdown";
import useClickToOpenMenu from "components/bootstrap/useClickToOpenMenu";
import Icon from "components/common/Icon";
import ActiveSliceColContext from "components/common/EntityDataTable/contexts/ActiveSliceColContext";
import useMergedRef from "util/hooks/useMergedRef";
import SortIcon from "../SortIcon";
export const DragIcon = styled(Icon)(
  ({ theme, $isDragging }) => css`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%) rotate(90deg);
    color: ${theme.colors.text.secondary};
    opacity: ${$isDragging ? 1 : 0};
    transition: opacity 0.15s ease-in-out;
    pointer-events: none;
    font-size: 12px;
  `
);
export const ThInner = styled.div(
  ({ $isDraggable, $isDragging, $isResizingAnyColumn }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    position: relative;
    padding: ${CELL_PADDING_HEADER};

    ${!$isResizingAnyColumn && css`
      &:hover .header-action {
        opacity: 1;
      }
    `}

    /* pointer, not grab, below: a plain click opens the actions menu, which is the more common
       interaction, so the cursor hints at that instead of the (still available) drag-to-reorder. */
    ${$isDraggable && css`
      cursor: ${$isDragging ? "grabbing" : "pointer"};

      &:focus-visible .header-action {
        opacity: 1;
      }
    `}
  `
);
export const LeftCol = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
export const IndicatorCol = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
const ActiveSliceIcon = styled(Icon)(
  ({ theme, $isRightAligned }) => css`
    ${$isRightAligned ? css`
          margin-right: ${theme.spacings.xs};
        ` : css`
          margin-left: ${theme.spacings.xs};
        `}
    color: ${theme.colors.gray[20]};
  `
);
const useSortableCol = (colId, disabled) => {
  const { setColumnTransform } = useContext(DndStylesContext);
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: colId,
    disabled
  });
  const cssTransform = CSS.Translate.toString(transform);
  useLayoutEffect(() => {
    setColumnTransform((cur) => {
      if (cur[colId] === cssTransform) return cur;
      return { ...cur, [colId]: cssTransform };
    });
  }, [colId, setColumnTransform, cssTransform]);
  return {
    attributes,
    isDragging,
    listeners,
    setNodeRef
  };
};
const AttributeHeader = ({
  ctx,
  onHeaderSectionResize,
  onChangeSlicing,
  appSection
}) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation("common");
  const activeSliceCol = useContext(ActiveSliceColContext);
  const colId = ctx.header.column.id;
  const columnMeta = ctx.column.columnDef.meta;
  const isDraggable = Boolean(columnMeta == null ? void 0 : columnMeta.enableColumnOrdering);
  const isResizingAnyColumn = Boolean(ctx.table.getState().columnSizingInfo.isResizingColumn);
  const { attributes, isDragging, listeners, setNodeRef } = useSortableCol(colId, !isDraggable);
  const { triggerRef, opened, onOpenChange, anchorPosition, onClick, onKeyDown } = useClickToOpenMenu();
  const mergedHeaderRef = useMergedRef(setNodeRef, triggerRef);
  const leftRef = useHeaderSectionObserver(colId, "left", onHeaderSectionResize);
  const rightRef = useHeaderSectionObserver(colId, "right", onHeaderSectionResize);
  const columnLabel = (_a = columnMeta == null ? void 0 : columnMeta.label) != null ? _a : colId;
  const canSlice = columnMeta == null ? void 0 : columnMeta.enableSlicing;
  const isSliceActive = activeSliceCol === colId;
  const canSort = ctx.header.column.getCanSort();
  const canHideColumn = ctx.header.column.getCanHide();
  const sortDirection = ctx.header.column.getIsSorted();
  const textAlign = (_b = columnMeta == null ? void 0 : columnMeta.columnRenderer) == null ? void 0 : _b.textAlign;
  const isRightAligned = textAlign === "right";
  const dragTitle = typeof columnLabel === "string" ? `${DRAG_HANDLE_DEFAULT_TITLE} ${columnLabel.toLocaleLowerCase()}` : DRAG_HANDLE_DEFAULT_TITLE;
  const hasHeaderActions = Boolean(canSort || canSlice || canHideColumn);
  const onHeaderKeyDown = (event) => {
    var _a2;
    (_a2 = listeners == null ? void 0 : listeners.onKeyDown) == null ? void 0 : _a2.call(listeners, event);
    if (isDragging || !hasHeaderActions) {
      return;
    }
    onKeyDown(event);
  };
  const sliceIndicator = isSliceActive && /* @__PURE__ */ React.createElement(
    ActiveSliceIcon,
    {
      name: "surgical",
      title: t("entity_table.slicing_by", { column: columnLabel }),
      size: "xs",
      $isRightAligned: isRightAligned
    }
  );
  const sortIndicator = sortDirection && /* @__PURE__ */ React.createElement(SortIcon, { column: ctx.header.column });
  const indicatorIcons = (
    // Stops the sort icon's own click from also bubbling up to the header's onClick (which would
    // toggle the actions menu open/closed right after the sort click already did its own thing).
    /* @__PURE__ */ React.createElement(IndicatorCol, { ref: rightRef, onClick: (event) => event.stopPropagation() }, isRightAligned ? /* @__PURE__ */ React.createElement(React.Fragment, null, sortIndicator, sliceIndicator) : /* @__PURE__ */ React.createElement(React.Fragment, null, sliceIndicator, sortIndicator))
  );
  const titleGroup = /* @__PURE__ */ React.createElement(LeftCol, { ref: leftRef }, /* @__PURE__ */ React.createElement(
    HeaderActionsDropdown,
    {
      textAlign,
      label: columnLabel,
      activeSort: sortDirection,
      isSliceActive,
      onChangeSlicing: canSlice ? onChangeSlicing : void 0,
      sliceColumnId: colId,
      appSection,
      onSort: canSort ? (desc) => ctx.table.setSorting([{ id: colId, desc }]) : void 0,
      onHideColumn: canHideColumn ? () => ctx.header.column.toggleVisibility() : void 0,
      opened,
      onOpenChange,
      anchorPosition
    },
    (_e = (_d = (_c = columnMeta == null ? void 0 : columnMeta.columnRenderer) == null ? void 0 : _c.renderHeader) == null ? void 0 : _d.call(_c, columnLabel)) != null ? _e : columnLabel
  ));
  const nonDraggableA11yProps = !isDraggable && hasHeaderActions ? { role: "button", tabIndex: 0 } : {};
  return /* @__PURE__ */ React.createElement(
    ThInner,
    {
      ref: mergedHeaderRef,
      $isDraggable: isDraggable,
      $isDragging: isDragging,
      $isResizingAnyColumn: isResizingAnyColumn,
      title: isDraggable ? dragTitle : void 0,
      "aria-label": isDraggable ? dragTitle : void 0,
      ...isDraggable ? { ...attributes, ...listeners } : {},
      ...nonDraggableA11yProps,
      onClick: hasHeaderActions ? onClick : void 0,
      onKeyDown: isDraggable || hasHeaderActions ? onHeaderKeyDown : void 0
    },
    isDraggable && /* @__PURE__ */ React.createElement(DragIcon, { name: "drag_indicator", size: "xs", $isDragging: isDragging, className: "header-action" }),
    isRightAligned ? /* @__PURE__ */ React.createElement(React.Fragment, null, indicatorIcons, titleGroup) : /* @__PURE__ */ React.createElement(React.Fragment, null, titleGroup, indicatorIcons)
  );
};
const useAttributeColumnDefinitions = ({
  columnHelper,
  columnRenderersByAttribute,
  columnSchemas,
  columnWidths,
  entityAttributesAreCamelCase,
  enableSlicing,
  meta,
  onChangeSlicing,
  onHeaderSectionResize,
  appSection
}) => {
  const cell = useCallback(
    ({
      row,
      getValue,
      column
    }) => {
      var _a, _b, _c;
      const columnDefMeta = column.columnDef.meta;
      return (_c = (_b = (_a = columnDefMeta == null ? void 0 : columnDefMeta.columnRenderer) == null ? void 0 : _a.renderCell) == null ? void 0 : _b.call(_a, getValue(), row.original, meta)) != null ? _c : getValue();
    },
    [meta]
  );
  const header = useCallback(
    (ctx) => /* @__PURE__ */ React.createElement(
      AttributeHeader,
      {
        ctx,
        onHeaderSectionResize,
        onChangeSlicing,
        appSection
      }
    ),
    [appSection, onChangeSlicing, onHeaderSectionResize]
  );
  return useMemo(
    () => columnSchemas.map((col) => {
      var _a;
      const baseColDef = {
        id: col.id,
        cell,
        header,
        size: columnWidths[col.id],
        enableHiding: true,
        enableResizing: true,
        meta: {
          label: col.title,
          enableSlicing: enableSlicing && col.sliceable,
          columnRenderer: columnRenderersByAttribute[col.id],
          enableColumnOrdering: true
        }
      };
      if (col.isDerived) {
        return columnHelper.display(baseColDef);
      }
      const attributeName = entityAttributesAreCamelCase ? camelCase(col.id) : col.id;
      return columnHelper.accessor((row) => row[attributeName], {
        enableSorting: (_a = col.sortable) != null ? _a : false,
        ...baseColDef
      });
    }),
    [
      columnSchemas,
      cell,
      header,
      columnWidths,
      columnRenderersByAttribute,
      enableSlicing,
      entityAttributesAreCamelCase,
      columnHelper
    ]
  );
};
export default useAttributeColumnDefinitions;
